import React from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { DeviceSpecificFields } from '../../common/DeviceSpecificFields';
import { TemplateFieldsEditor } from '../TemplateFieldsEditor';
import { ConfigFieldsTable } from './ConfigFieldsTable';
import { TemplateDetailState } from './useTemplateDetail';

/** The product's base configuration, folded away under the values. */
const InheritedConfig: React.FC<{ deviceType: string; productConfig: Record<string, unknown> }> = ({ deviceType, productConfig }) => {
  const { t } = useI18n();
  if (Object.keys(productConfig).length === 0) return null;
  return (
    <details className="template-inheritance">
      <summary>{t('configTemplates.inheritsFrom', { code: deviceType })}</summary>
      <pre className="json-preview">{JSON.stringify(productConfig, null, 2)}</pre>
    </details>
  );
};

/** The Configuration tab: the template's values, read or edited against the gateway schema. */
export const TemplateConfigTab: React.FC<{ state: TemplateDetailState }> = ({ state }) => {
  const { t } = useI18n();
  const { template, isEditMode, isLoading, deviceType, productConfig } = state;
  if (!template) return null;

  if (!isEditMode) {
    return (
      <div className="tab-content">
        <section className="cu-section">
          <header className="cu-section-head">
            <h3>{t('configTemplates.detail.configTitle')}</h3>
            <p>{t('configTemplates.detail.configIntro')}</p>
          </header>
          <ConfigFieldsTable jsonConfig={template.json_config} customFields={state.customFields} canRevealPassword={state.canRevealPassword} />
          <InheritedConfig deviceType={deviceType} productConfig={productConfig} />
        </section>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <section className="cu-section">
        <header className="cu-section-head">
          <h3>{t('configTemplates.detail.configTitle')}</h3>
          <p>{t('configTemplates.layersHint')}</p>
        </header>
        {state.schemaLoading && (
          <div className="tpd-empty"><p>{t('configTemplates.detail.schemaLoading', { type: deviceType })}</p></div>
        )}
        {!state.schemaLoading && state.schema && state.schema.length === 0 && (
          <div className="tpd-empty">
            <p><strong>{t('configTemplates.detail.noSchema', { type: deviceType })}</strong></p>
            <p>{t('configTemplates.detail.noSchemaHint')}</p>
          </div>
        )}
        {!state.schemaLoading && state.groupedSchema.length > 0 && (
          <TemplateFieldsEditor
            groups={state.groupedSchema}
            values={state.fieldValues}
            errors={state.errors}
            disabled={isLoading}
            onChange={state.setFieldValue}
            password={state.passwordInSchema ? {
              generate: state.generatePassword,
              value: state.password,
              placeholder: t('configTemplates.leaveEmptyKeepCurrent'),
              onGenerateChange: (generate) => { state.setGeneratePassword(generate); if (generate) state.setPassword(''); },
              onValueChange: state.setPassword,
            } : undefined}
          />
        )}
        <div className="tpd-custom">
          <DeviceSpecificFields fields={state.customFields} onChange={state.setCustomFields} isEditing />
        </div>
        <InheritedConfig deviceType={deviceType} productConfig={productConfig} />
      </section>
    </div>
  );
};
